import React, { useContext, useEffect } from "react";
import EcoScanContent from "../EcoScanContent";
import { useIntl } from "react-intl";
import { StyledTableHeader } from "../EcoScan.styles";
import { Box } from "@mui/material";
import { useTheme } from "@mui/styles";
import Percent from "components/Percent/Percent";
import { transformRate } from "features/helpers/utils";
import { IconButton } from "@metacrm/metacrm-material-ui/dist/Button";
import PopperMenu from "@metacrm/metacrm-material-ui/dist/PopperMenu";
import useEcoScanState from "features/metadesk/EcoScan/hooks/useEcoScanState.hooks";
import LineChartContainer from "../LineChartContainer";
import useNavigateEntity from "hooks/useNavigateEntity";
import EcoScanContext from "features/metadesk/EcoScan/contexts/EcoScan.context";
import { isEmpty } from "lodash-es";
import { useDebounceEffect } from "components/ConnectWallet/ConnectWallet";
import ChartLoadingPlaceholder from "../ChartLoadingPlaceholder";

const OverviewContainer = ({ type }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [anchorProjectName, setAnchorProjectName] = React.useState(null);
  const { projectPartyData, projectPartyDataLoading, fetchMorphLineData } =
    useEcoScanState();

  const { dateRange, selectedParty, setSelectedParty, overviewFilterValue } =
    useContext(EcoScanContext);

  const { navigateEntity } = useNavigateEntity();

  const headCellsData = [
    {
      show: true,
      disabledSort: true,
      id: "entityName",
      label: (
        <StyledTableHeader sx={{ paddingLeft: "8px" }}>
          {formatMessage({ id: "ecoScan.overview.entityNameColumn" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "activeWalletOnChain",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "ecoScan.header.awOnChain" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "activeWalletOnWebsite",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "ecoScan.header.awOnWebsite" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "transactionCount",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "global.metrics.transaction" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "newDiscordMember",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "ecoScan.header.newDiscordMember" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "newTelegramMember",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "ecoScan.header.newTelegramMember" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: false,
      id: "sessionsFromFootprints",
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "ecoScan.header.sessionsFootprints" })}
        </StyledTableHeader>
      ),
    },
    {
      show: true,
      disabledSort: true,
      id: "actions",
      maxWidth: 80,
      label: (
        <StyledTableHeader>
          {formatMessage({ id: "global.header.actions" })}
        </StyledTableHeader>
      ),
    },
  ];

  const handleClickActions = (event, projectItem) => {
    setAnchorEl(event.currentTarget);
    setAnchorProjectName(projectItem.entityName);
  };

  const handleCloseActions = () => {
    setAnchorEl(null);
    setAnchorProjectName(null);
  };

  const actionsList = [
    {
      children: (
        <Box>
          {formatMessage({
            id: "ecoScan.overview.actionCommunityAnalysisOption",
          })}
        </Box>
      ),
      func: (e) => {
        navigateEntity(
          `/ecoScan/community-analysis/detail/${anchorProjectName}`
        );
      },
    },
    {
      children: (
        <Box>
          {" "}
          {formatMessage({
            id: "ecoScan.overview.actionWebsiteTrafficOption",
          })}
        </Box>
      ),
      func: (e) => {
        navigateEntity(`/ecoScan/website-traffic/detail/${anchorProjectName}`);
      },
    },
    {
      children: (
        <Box>
          {" "}
          {formatMessage({
            id: "ecoScan.overview.actionOnChainOption",
          })}
        </Box>
      ),
      func: (e) => {
        navigateEntity(`/ecoScan/dapp-tracking/detail/${anchorProjectName}`);
      },
    },
  ];

  const tableContents = (projectItem) => {
    return [
      <Box
        slot="entityName"
        sx={{
          fontWeight: "600",
          color: theme.customColors.grey[900],
          paddingLeft: "8px",
        }}
      >
        {projectItem.entityName}
      </Box>,
      <Box slot="activeWalletOnChain">{projectItem.activeWalletOnChain}</Box>,
      <Box
        slot="activeWalletOnWebsite"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Box mr="4px">{projectItem.activeWalletOnWebsite}</Box>
        <Box>
          <Percent
            data={transformRate(projectItem.activeWalletOnWebsiteGrowthRate)}
          />{" "}
        </Box>
      </Box>,
      <Box
        slot="transactionCount"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Box mr="4px">{projectItem.transactionCount}</Box>
        <Box>
          <Percent
            data={transformRate(projectItem.transactionCountGrowthRate)}
          />
        </Box>
      </Box>,
      <Box
        slot="newDiscordMember"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Box mr="4px">{projectItem.newDiscordMember}</Box>
        <Box>
          <Percent
            data={transformRate(projectItem.newDiscordMemberGrowthRate)}
          />
        </Box>
      </Box>,
      <Box
        slot="newTelegramMember"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Box mr="4px">{projectItem.newTelegramMember}</Box>
        <Box>
          <Percent
            data={transformRate(projectItem.newTelegramMemberGrowthRate)}
          />
        </Box>
      </Box>,
      <Box
        slot="sessionsFromFootprints"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Box mr="4px">{projectItem.sessionsFromFootprints}</Box>
        <Box>
          <Percent
            data={transformRate(projectItem.sessionsFromFootprintsGrowthRate)}
          />
        </Box>
      </Box>,
      <Box slot="actions">
        {" "}
        <IconButton
          onClick={(e) => handleClickActions(e, projectItem)}
          size={24}
          color={theme.customColors.purple[500]}
        >
          <i className="meta-crm-icon-ic_dots font-size-24" />
        </IconButton>
      </Box>,
    ];
  };

  useDebounceEffect(
    () => {
      const startDate = dateRange[0];
      const endDate = dateRange[1];

      fetchMorphLineData({
        from: startDate,
        to: endDate,
        types: [overviewFilterValue],
        entities: selectedParty,
        page: "overview",
      });
    },
    1000,
    [selectedParty, dateRange, overviewFilterValue]
  );

  //   const projectPartyNames = Object.keys(projectPartyData[0]).filter(
  //     (key) => key !== "date"
  //   );

  const renderChartArea = () => {
    if (isEmpty(projectPartyData)) {
      return Array.from({ length: 1 }).map((index) => (
        <ChartLoadingPlaceholder
          item
          xs={12}
          key={index}
          sx={{ height: "315px" }}
        ></ChartLoadingPlaceholder>
      ));
    } else {
      return Object.entries(projectPartyData).map(([key, value]) => (
        <LineChartContainer
          type={type}
          key={key}
          title={key}
          data={value}
          isLoading={projectPartyDataLoading}
          projectPartyDataLength={Object.keys(projectPartyData).length}
        />
      ));
    }
  };

  return (
    <div>
      <EcoScanContent
        tableHeaders={headCellsData}
        tableContents={tableContents}
        renderChartArea={renderChartArea}
        isOverview
        type={type}
      ></EcoScanContent>
      <PopperMenu
        placement="bottom-end"
        open={Boolean(anchorEl)}
        onClose={handleCloseActions}
        anchorEl={anchorEl}
        itemList={actionsList}
      />
    </div>
  );
};

export default OverviewContainer;
